import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../app/store/store';
import {
  setNotification,
  setErrorNotification,
} from '../../../app/store/notifications';
import {
  fetchHelpdeskSettings,
  updateHelpdeskSettings,
  fetchRoutingRules,
  createRoutingRule,
  updateRoutingRule,
  deleteRoutingRule,
  reorderRoutingRules,
} from '../../../entities/helpdesk-settings/model';
import { SettingsToggle } from '@shared/ui/settings-toggle';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
  Branch,
  Mailbox,
  RoutingRule,
  UserBrief,
} from '../../../types/helpdeskSettingsInterfaces';
import { helpdeskSettingsApi } from '../../../api/services/helpdesk-settings.service';
import { modalCls } from '@shared/ui/form-modal';

/* ── Имя оператора и описание правила ── */
const briefName = (o: UserBrief): string =>
  `${o.last_name} ${o.first_name}`.trim() || o.username;

const ruleDescription = (rule: RoutingRule): string => {
  const ch =
    rule.channel === 'email'
      ? 'Email'
      : rule.channel === 'web'
      ? 'Веб-портал'
      : 'Любой канал';
  const ops = rule.operators?.length
    ? rule.operators.map(briefName).join(', ')
    : 'операторы не выбраны';
  const mb = rule.mailbox_detail ? ` (${rule.mailbox_detail.address})` : '';
  const br = rule.branch_detail ? ` → филиал «${rule.branch_detail.name}»` : '';
  return `${ch}${mb}${br} → ${ops}`;
};

/* ── Sortable-элемент правила ── */
interface SortableRuleProps {
  rule: RoutingRule;
  index: number;
  onEdit: (rule: RoutingRule) => void;
  onToggle: (rule: RoutingRule, active: boolean) => void;
}

const SortableRule: React.FC<SortableRuleProps> = ({ rule, index, onEdit, onToggle }) => {
  // dnd-kit (в этой версии) типизирует id как `string`, поэтому
  // приводим число к строке всюду, где id уходит в библиотеку.
  // Эквивалентность сохраняем за счёт того, что и SortableContext, и
  // обработчик handleDragEnd используют ту же строковую версию.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: String(rule.id),
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    // transition приходит как string | null, а CSSProperties ждёт
    // string | undefined. null → undefined, иначе ругается TS.
    transition: transition ?? undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-4 py-4 border-b border-gray-100 last:border-b-0"
    >
      {/* Drag handle */}
      <button {...attributes} {...listeners} className="cursor-grab text-gray-300 hover:text-gray-500 touch-none">
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M7 2a2 2 0 10.001 4.001A2 2 0 007 2zm0 6a2 2 0 10.001 4.001A2 2 0 007 8zm0 6a2 2 0 10.001 4.001A2 2 0 007 14zm6-8a2 2 0 10-.001-4.001A2 2 0 0013 6zm0 2a2 2 0 10.001 4.001A2 2 0 0013 8zm0 6a2 2 0 10.001 4.001A2 2 0 0013 14z" />
        </svg>
      </button>

      {/* Number */}
      <span className="w-8 h-8 flex items-center justify-center bg-gray-100 text-sm font-semibold text-[#2d2c2c] rounded-full shrink-0">
        {index + 1}
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#2d2c2c] truncate">{rule.name}</p>
        <p className="text-xs text-gray-400 truncate mt-0.5">{ruleDescription(rule)}</p>
      </div>

      {/* Toggle + Edit */}
      <div className="flex items-center gap-3 shrink-0">
        <button
          type="button"
          role="switch"
          aria-checked={rule.is_active}
          onClick={() => onToggle(rule, !rule.is_active)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
            rule.is_active ? 'bg-[#e3282a]' : 'bg-gray-300'
          }`}
        >
          <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
            rule.is_active ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
        <button onClick={() => onEdit(rule)}
          className="text-sm font-medium text-[#e3282a] hover:text-[#c82022] transition-colors">
          Изменить
        </button>
      </div>
    </div>
  );
};

/* ── Модалка создания/редактирования ── */
interface RuleModalProps {
  rule?: RoutingRule | null;
  onClose: () => void;
  onSave: (data: Partial<RoutingRule>) => void;
  onDelete?: () => void;
}

/* Список операторов с чекбоксами. На уровне модуля намеренно: компонент,
   объявленный внутри модалки, пересоздавался на каждом рендере и
   перемонтировал список (сброс прокрутки при каждом клике). */
type PickerUser = { id: number; username: string; first_name: string; last_name: string; full_name: string };

const opName = (o: { first_name: string; last_name: string; full_name: string; username: string }) =>
  o.full_name || `${o.last_name} ${o.first_name}`.trim() || o.username;

const Picker: React.FC<{
  operators: PickerUser[];
  selected: number[];
  onPick: (id: number) => void;
}> = ({ operators, selected, onPick }) => (
  <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg divide-y divide-gray-100">
    {operators.length === 0 ? (
      <p className="text-xs text-gray-400 px-3 py-3">Нет доступных операторов</p>
    ) : (
      operators.map((o) => (
        <label
          key={o.id}
          className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50"
        >
          <input
            type="checkbox"
            checked={selected.includes(o.id)}
            onChange={() => onPick(o.id)}
            className="w-4 h-4 rounded border-gray-300 focus:ring-red-400"
          />
          <span className="text-sm text-[#2d2c2c]">{opName(o)}</span>
        </label>
      ))
    )}
  </div>
);

const RuleModal: React.FC<RuleModalProps> = ({ rule, onClose, onSave, onDelete }) => {
  const [channel, setChannel] = useState<string>(rule?.channel || '');
  // Ступень 1 маршрутизации: ящик, на который пришло письмо → филиал.
  const [mailboxId, setMailboxId] = useState<string>(
    rule?.mailbox ? String(rule.mailbox) : '',
  );
  const [branchId, setBranchId] = useState<string>(
    rule?.branch ? String(rule.branch) : '',
  );
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [operatorIds, setOperatorIds] = useState<number[]>(
    rule?.operators?.map((o) => o.id) || [],
  );
  const [fallbackIds, setFallbackIds] = useState<number[]>(
    rule?.fallback_operators?.map((o) => o.id) || [],
  );
  const [operators, setOperators] = useState<PickerUser[]>([]);

  useEffect(() => {
    helpdeskSettingsApi
      .getAvailableOperators()
      .then((res) => setOperators(res.data))
      .catch(() => setOperators([]));
    helpdeskSettingsApi
      .getMailboxes()
      .then((res) => setMailboxes(res.data))
      .catch(() => setMailboxes([]));
    helpdeskSettingsApi
      .getBranches()
      .then((res) => setBranches(res.data))
      .catch(() => setBranches([]));
  }, []);

  const toggle = (
    list: number[],
    setList: React.Dispatch<React.SetStateAction<number[]>>,
    id: number,
  ) => {
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      channel: channel as RoutingRule['channel'],
      mailbox: mailboxId ? Number(mailboxId) : null,
      branch: branchId ? Number(branchId) : null,
      operator_ids: operatorIds,
      fallback_operator_ids: fallbackIds,
    });
  };

  return (
    <div className={modalCls.overlay}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className={`${modalCls.panel} max-w-md`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-lg font-semibold text-[#2d2c2c]">
            {rule ? 'Изменить сценарий' : 'Добавить сценарий'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className={modalCls.body}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Канал</label>
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
            >
              <option value="">Любой</option>
              <option value="email">Email</option>
              <option value="web">Веб-портал</option>
            </select>
          </div>

          {channel !== 'web' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Почтовый ящик
              </label>
              <p className="text-xs text-gray-400 mb-2">
                Правило сработает только для писем на этот адрес
              </p>
              <select
                value={mailboxId}
                onChange={(e) => setMailboxId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
              >
                <option value="">Любой ящик</option>
                {mailboxes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.address}
                    {m.branch_name ? ` — ${m.branch_name}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Филиал</label>
            <p className="text-xs text-gray-400 mb-2">
              Заявка будет отнесена к филиалу; если операторы не выбраны — уйдёт
              дежурному оператору филиала
            </p>
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
            >
              <option value="">По ящику / по умолчанию</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ответственные операторы (группа)
            </label>
            <p className="text-xs text-gray-400 mb-2">
              Заявка уйдёт наименее загруженному из тех, кто сейчас на смене
            </p>
            <Picker
              operators={operators}
              selected={operatorIds}
              onPick={(id) => toggle(operatorIds, setOperatorIds, id)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ответственные (если никого нет на смене)
            </label>
            <p className="text-xs text-gray-400 mb-2">
              Резерв, чтобы заявка не осталась без ответственного
            </p>
            <Picker
              operators={operators}
              selected={fallbackIds}
              onPick={(id) => toggle(fallbackIds, setFallbackIds, id)}
            />
          </div>

          <div className={`flex justify-between ${modalCls.footerSticky}`}>
            <div>
              {rule && onDelete && (
                <button
                  type="button"
                  onClick={onDelete}
                  className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800"
                >
                  Удалить
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Отмена
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022]"
              >
                {rule ? 'Сохранить' : 'Добавить'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Основной экран ── */
const RoutingSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { settings, status, routingRules, rulesStatus } = useSelector(
    (state: RootState) => state.helpdeskSettings,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<RoutingRule | null>(null);
  const [localRules, setLocalRules] = useState<RoutingRule[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  useEffect(() => {
    if (status === 'idle') dispatch(fetchHelpdeskSettings());
    if (rulesStatus === 'idle') dispatch(fetchRoutingRules());
  }, [dispatch, status, rulesStatus]);

  useEffect(() => {
    setLocalRules(routingRules);
  }, [routingRules]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      // active.id / over.id у dnd-kit — UniqueIdentifier (string | number),
      // в этой версии приходит строкой. Сравниваем по строке, а индекс
      // ищем по числовой версии rule.id.
      const oldIndex = localRules.findIndex((r) => String(r.id) === String(active.id));
      const newIndex = localRules.findIndex((r) => String(r.id) === String(over.id));
      const reordered = arrayMove(localRules, oldIndex, newIndex);
      setLocalRules(reordered);
      dispatch(reorderRoutingRules(reordered.map((r) => r.id)))
        .unwrap()
        .then(() => dispatch(setNotification('Порядок сценариев обновлён')))
        .catch((err: unknown) => {
          dispatch(
            setErrorNotification(
              typeof err === 'string' ? err : 'Не удалось обновить порядок'
            )
          );
          // Откатываем оптимистичный порядок на сервиерный, чтобы UI
          // не висел в неправильном состоянии.
          setLocalRules(routingRules);
        });
    },
    [dispatch, localRules],
  );

  const handleToggle = useCallback(
    (rule: RoutingRule, active: boolean) => {
      dispatch(updateRoutingRule({ id: rule.id, data: { is_active: active } }));
    },
    [dispatch],
  );

  const handleSave = useCallback(
    async (data: Partial<RoutingRule>) => {
      const isEdit = Boolean(editingRule);
      try {
        if (editingRule) {
          await dispatch(
            updateRoutingRule({ id: editingRule.id, data })
          ).unwrap();
        } else {
          await dispatch(
            createRoutingRule({ ...data, order_index: localRules.length })
          ).unwrap();
        }
        // Тост даём ровно один — фронт раньше сохранял молча и было
        // непонятно, прошло ли действие.
        dispatch(
          setNotification(
            isEdit ? 'Сценарий обновлён' : 'Сценарий добавлен'
          )
        );
        setModalOpen(false);
        setEditingRule(null);
        dispatch(fetchRoutingRules());
      } catch (err: unknown) {
        dispatch(
          setErrorNotification(
            typeof err === 'string'
              ? err
              : isEdit
              ? 'Не удалось обновить сценарий'
              : 'Не удалось добавить сценарий'
          )
        );
      }
    },
    [dispatch, editingRule, localRules.length],
  );

  const handleDelete = useCallback(async () => {
    if (!editingRule) return;
    try {
      await dispatch(deleteRoutingRule(editingRule.id)).unwrap();
      dispatch(setNotification('Сценарий удалён'));
      setModalOpen(false);
      setEditingRule(null);
    } catch (err: unknown) {
      dispatch(
        setErrorNotification(
          typeof err === 'string' ? err : 'Не удалось удалить сценарий'
        )
      );
    }
  }, [dispatch, editingRule]);

  const handleSettingsUpdate = useCallback(
    (field: string, value: boolean) => {
      dispatch(updateHelpdeskSettings({ [field]: value }));
    },
    [dispatch],
  );

  if ((status === 'loading' && !settings) || (rulesStatus === 'loading' && localRules.length === 0)) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-1/3"></div>
          <div className="h-16 bg-gray-100 rounded"></div>
          <div className="h-16 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Сценарии маршрутизации.
          Заголовок «Сценарии маршрутизации» переехал в шапку приложения,
          здесь оставляем функциональную подсказку и action-кнопку. */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-500">
            Заявки распределяются по первому подходящему правилу сверху вниз
          </p>
          <button onClick={() => { setEditingRule(null); setModalOpen(true); }}
            className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] transition-colors">
            + Добавить сценарий
          </button>
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          {/* SortableContext.items в этой версии ждёт string[] —
              приводим явно. */}
          <SortableContext items={localRules.map((r) => String(r.id))} strategy={verticalListSortingStrategy}>
            {localRules.map((rule, idx) => (
              <SortableRule
                key={rule.id}
                rule={rule}
                index={idx}
                onEdit={(r) => { setEditingRule(r); setModalOpen(true); }}
                onToggle={handleToggle}
              />
            ))}
          </SortableContext>
        </DndContext>

        {localRules.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">
            Нет правил маршрутизации. Добавьте первый сценарий.
          </p>
        )}
      </div>

      {/* Очередь при отсутствии операторов */}
      {settings && (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-base font-semibold text-[#2d2c2c] mb-1">
            Очередь при отсутствии операторов
          </h2>
          <p className="text-sm text-gray-400 mb-4">
            Если ни один оператор не доступен по расписанию
          </p>
          <div>
            <SettingsToggle
              label="Уведомить Администратора и Ведущего оператора"
              description="При попадании заявки в общую очередь"
              checked={settings.notify_admin_on_unassigned}
              onChange={(v) => handleSettingsUpdate('notify_admin_on_unassigned', v)}
            />
            <SettingsToggle
              label="Автоназначение при появлении свободного оператора"
              description="Заявка из очереди назначается автоматически"
              checked={settings.auto_assign_on_available}
              onChange={(v) => handleSettingsUpdate('auto_assign_on_available', v)}
            />
          </div>
        </div>
      )}

      {/* Модалка */}
      {modalOpen && (
        <RuleModal
          rule={editingRule}
          onClose={() => { setModalOpen(false); setEditingRule(null); }}
          onSave={handleSave}
          onDelete={editingRule ? handleDelete : undefined}
        />
      )}
    </div>
  );
};

export default RoutingSettings;
